import { useTRPC } from "../../../trpc";
import { Card, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { useIntl } from "react-intl";
import { DatasetItem } from "./components/DatasetItem";
import { DatasetListControls } from "./components/DatasetListControls";
import { DatasetItemCondensed } from "./components/DatasetItemCondensed";
import { useDebouncedValue, useLocalStorage } from "@mantine/hooks";
import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DatasetWithCurrentVersion } from "./types/dataset";
import { useLocale } from "../../../hooks/locale";
import { getLocaleString } from "@project/common";

const sortByDate = (a: DatasetWithCurrentVersion, b: DatasetWithCurrentVersion) => {
  return new Date(a.dataset.createdAt).getTime() - new Date(b.dataset.createdAt).getTime();
};

function sortDatasets(datasets: DatasetWithCurrentVersion[]): DatasetWithCurrentVersion[] {
  const privateDatasets = datasets.filter(d => d.dataset.scope === "private").sort(sortByDate);
  const publicDatasets = datasets.filter(d => d.dataset.scope === "public").sort(sortByDate);
  return [...privateDatasets, ...publicDatasets];
}

export function Datasets() {
  const intl = useIntl();
  const trpc = useTRPC();
  const locale = useLocale();

  const { data: datasets = [] } = useQuery(trpc.getDatasetsWithCurrentVersion.queryOptions(undefined, {
    refetchInterval: 1000 * 60 * 5,
  }));
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);

  const [isCondensedView, setIsCondensedView] = useLocalStorage({
    key: "dataset-view-mode",
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  const toggleView = useCallback(() => {
    setIsCondensedView(!isCondensedView);
  }, [isCondensedView, setIsCondensedView]);

  const filteredDatasets = useMemo(() => {
    if (!debouncedQuery.trim()) return sortDatasets(datasets);

    const query = debouncedQuery.toLowerCase().trim();
    const filtered = datasets.filter((item) => {
      const { dataset: { key, displayName, description } } = item;

      if (key.toLowerCase().includes(query)) return true;
      if (getLocaleString(locale, displayName).toLowerCase().includes(query)) return true;
      if (getLocaleString(locale, description).toLowerCase().includes(query)) return true;

      return false;
    });
    return sortDatasets(filtered);
  }, [datasets, debouncedQuery, locale]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Home", id: "navigation.home" })}
        current={intl.formatMessage({ defaultMessage: "Datasets", id: "navigation.datasets" })}
        description={intl.formatMessage({ defaultMessage: "View and manage your datasets.", id: "text.datasetsOverviewDescription" })}
      />

      <DatasetListControls
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isCondensedView={isCondensedView}
        toggleView={toggleView}
      />

      <Stack gap={isCondensedView ? "xs" : "md"}>
        {isCondensedView
          ? (
              filteredDatasets.map((datasetWithVersion) => {
                const { dataset, version, account } = datasetWithVersion;

                return (
                  <DatasetItemCondensed
                    key={dataset.id}
                    dataset={dataset}
                    version={version}
                    account={account}
                  />
                );
              })
            )
          : (
              filteredDatasets.map((datasetWithVersion) => {
                const { dataset, version, account } = datasetWithVersion;

                return (
                  <DatasetItem
                    key={dataset.id}
                    dataset={dataset}
                    version={version}
                    account={account}
                  />
                );
              })
            )}

        {filteredDatasets.length === 0 && (
          <Card withBorder p="xl" ta="center">
            {searchQuery
              ? (
                  intl.formatMessage({
                    defaultMessage: "No datasets found matching: {query}",
                    id: "text.noDatasetsFound",
                  }, { query: searchQuery })
                )
              : (
                  intl.formatMessage({
                    defaultMessage: "No datasets available.",
                    id: "text.noDatasets",
                  })
                )}
          </Card>
        )}
      </Stack>
    </Stack>
  );
}
